import React, { useState } from 'react';
import { Typography, Box, Button, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useNavigate, useLocation } from 'react-router-dom';
import { OnboardingLayout } from './onboard-layout';
import PinkGradientButton from '../../UIControls/PinkGradientButton';

// Helper function to build query string from params
const buildQueryString = (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return qs ? `?${qs}` : '';
};

const userDescriptionOptions = [
    { label: 'Physiotherapist', value: 'Physiotherapist', mixpanelTag: 'Physiotherapist' },
    { label: 'Chiropractor', value: 'Chiropractor', mixpanelTag: 'Chiropractor' },
    {
        label: 'Osteopath',
        value: 'Osteopath',
        mixpanelTag: 'Osteopath',
    },
    { label: 'Sports Therapist', value: 'Sports Therapist', mixpanelTag: 'Sports Therapist' },
    { label: 'Occupational Therapist', value: 'Occupational Therapist', mixpanelTag: 'Occupational Therapist' },
    {
        label: 'First Contact Practitioner',
        value: 'First Contact Practitioner',
        mixpanelTag: 'First Contact Practitioner',
    },
    { label: 'GP / Doctor / Consultant', value: 'GP / Doctor / Consultant', mixpanelTag: 'GP - Doctor - Consultant' },
    {
        label: 'Clinical Educator / Lecturer / Clinical Researcher',
        value: 'Clinical Educator / Lecturer / Clinical Researcher',
        mixpanelTag: 'Clinical Educator - Lecturer - Clinical Researcher',
    },
    { label: 'Receptionist / Admin', value: 'Receptionist / Admin', mixpanelTag: 'Receptionist - Admin' },
    { label: 'Student', value: 'Student', mixpanelTag: 'Student' },
    { label: 'Other', value: 'Other', mixpanelTag: 'Other' },
];

const OnboardUserDescription = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const isShowOffer = searchParams.get('LVS') === 'true';
    const [userData, setUserData] = useState({ ...location.state?.userData });
    const [selectedDescriptions, setSelectedDescriptions] = useState(userData.userDescription || []);

    const handleBack = async () => {
        const qs = buildQueryString({ ...(isShowOffer ? { LVS: 'true' } : {}) });
        navigate(`/onboard/type${qs}`, { state: { userData } });
    };

    const handleCheckboxChange = (value) => {
        setSelectedDescriptions((prev) => {
            if (prev.includes(value)) {
                return prev.filter((item) => item !== value);
            } else {
                return [...prev, value];
            }
        });
    };

    const handleNext = () => {
        const updatedUserData = {
            ...userData,
            userDescription: selectedDescriptions,
        };

        const nextStep = '/onboard/name';
        const qs = buildQueryString({ ...(isShowOffer ? { LVS: 'true' } : {}) });
        navigate(`${nextStep}${qs}`, { state: { userData: updatedUserData } });
    };

    const isNextButtonDisabled = selectedDescriptions.length === 0;

    const inputChildren = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormGroup>
                {userDescriptionOptions.map((option) => (
                    <FormControlLabel
                        key={option.value}
                        control={
                            <Checkbox
                                checked={selectedDescriptions.includes(option.value)}
                                onChange={() => handleCheckboxChange(option.value)}
                                sx={{
                                    color: '#E0E0E0',
                                    '&.Mui-checked': {
                                        color: '#ff4081',
                                    },
                                }}
                            />
                        }
                        label={
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {option.label}
                            </Typography>
                        }
                        sx={{
                            mb: 1,
                            borderRadius: '12px',
                            padding: '12px 16px',
                            border: '1px solid #E0E0E0',
                            marginLeft: 0,
                            marginRight: 0,
                            transition: 'all 0.2s',
                            backgroundColor: selectedDescriptions.includes(option.value)
                                ? 'rgba(255, 64, 129, 0.08)'
                                : 'white',
                            '&:hover': {
                                borderColor: '#ff4081',
                                backgroundColor: 'rgba(255, 64, 129, 0.04)',
                            },
                        }}
                    />
                ))}
            </FormGroup>
        </Box>
    );

    const renderButtonGroup = () => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                margin: 'auto',
                padding: '24px 0',
            }}
        >
            <Box onClick={handleBack} sx={{ display: 'flex', alignItems: 'center', width: 120 }}>
                <KeyboardBackspaceIcon />
                <Button
                    sx={{
                        color: '#000453',
                        textDecoration: 'underline',
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                >
                    Go back
                </Button>
            </Box>
            <PinkGradientButton
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={isNextButtonDisabled}
                sx={{ flexGrow: 1, marginLeft: 1, position: 'relative', maxWidth: 200, m: 0 }}
            >
                Next
            </PinkGradientButton>
        </Box>
    );

    return (
        <OnboardingLayout
            title="What describes you best?"
            subtitle="Select all that apply to help us personalise your experience."
            previousStepPath="/onboard/type"
            inputChildren={inputChildren}
            navChildren={renderButtonGroup()}
            currentStep={1}
            userData={userData}
            backgroundColor={false}
        />
    );
};

export default OnboardUserDescription;
